import React from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { formatLocalTime } from '../utils/timeUtils';

/* ── Full-page live log viewer (opened in a new tab from the match page) ──
 * Consumes the same per-match SSE endpoint external tools use
 * (GET /api/matches/:id/log-stream). The token is fetched with the admin's JWT,
 * so only the match owner / super admin can open this page.
 */
const MAX_LINES = 200000; // matches the server's ?history=all ceiling (a full BO1 is ~40k lines)

// The list is virtualized: only the rows in view (+ overscan) are mounted, at a fixed
// height, so 40k+ lines load, filter and scroll instantly. Long lines scroll horizontally.
const ROW_H = 19;
const OVERSCAN = 30;

const LogRow = React.memo(function LogRow({ row, top }) {
  return (
    <div title={row.line} style={{ position: 'absolute', top, left: 0, minWidth: '100%', height: ROW_H, display: 'flex', gap: '0.75rem', whiteSpace: 'pre', alignItems: 'center' }}>
      <span style={{ color: 'var(--text-muted, #4a4a5a)', flexShrink: 0 }}>{formatLocalTime(row.ts)}</span>
      <span style={{ color: '#5eead4', flexShrink: 0 }}>M{(row.map_index || 0) + 1}</span>
      <span style={{ color: 'var(--text-secondary, #c8c8d4)' }}>{row.line}</span>
    </div>
  );
});

export default function LiveLogViewer() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [status, setStatus] = React.useState('connecting'); // connecting | live | error | revoked | denied
  const [title, setTitle] = React.useState('');
  const [matchStatus, setMatchStatus] = React.useState(null); // matches.status from the stream (hello / status events)
  const [lines, setLines] = React.useState([]);
  const [paused, setPaused] = React.useState(false);
  const [filter, setFilter] = React.useState('');
  const esRef = React.useRef(null);
  const bufRef = React.useRef([]);
  const lastIdRef = React.useRef(0);
  const pausedRef = React.useRef(false);
  const flushTimerRef = React.useRef(null);
  const boxRef = React.useRef(null);

  const close = React.useCallback(() => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    if (flushTimerRef.current) { clearTimeout(flushTimerRef.current); flushTimerRef.current = null; }
  }, []);

  const scheduleFlush = React.useCallback(() => {
    if (flushTimerRef.current || pausedRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      setLines(bufRef.current.slice());
    }, 100);
  }, []);

  const connect = React.useCallback(async () => {
    close();
    setStatus('connecting');
    let token;
    try {
      const res = await axios.get(`/api/matches/${id}/log-stream-token`);
      token = res.data.log_stream_token;
    } catch (err) {
      setStatus(err.response && (err.response.status === 403 || err.response.status === 404) ? 'denied' : 'error');
      return;
    }
    // Resume after the last line we already have, otherwise replay the whole match
    const resume = lastIdRef.current > 0 ? `&last_id=${lastIdRef.current}` : '&history=all';
    const es = new EventSource(`/api/matches/${id}/log-stream?token=${encodeURIComponent(token)}${resume}`);
    esRef.current = es;
    es.addEventListener('hello', (e) => {
      setStatus('live');
      try {
        const h = JSON.parse(e.data);
        if (h.team1_name || h.team2_name) setTitle(`${h.team1_name || '?'} vs ${h.team2_name || '?'}`);
        if (h.status) setMatchStatus(h.status);
      } catch (_) {}
    });
    es.addEventListener('status', (e) => {
      try { const s = JSON.parse(e.data); if (s.status) setMatchStatus(s.status); } catch (_) {}
    });
    es.addEventListener('log', (e) => {
      let row;
      try { row = JSON.parse(e.data); } catch (_) { return; }
      if (!row || row.id <= lastIdRef.current) return; // de-dupe across auto-reconnects
      lastIdRef.current = row.id;
      bufRef.current.push(row);
      if (bufRef.current.length > MAX_LINES) bufRef.current.splice(0, bufRef.current.length - MAX_LINES);
      scheduleFlush();
    });
    es.addEventListener('revoked', () => { setStatus('revoked'); close(); });
    es.onopen = () => setStatus('live');
    es.onerror = () => {
      if (!esRef.current) return;
      // A rotated token makes the server answer 401 and EventSource gives up (CLOSED)
      setStatus(es.readyState === EventSource.CLOSED ? 'error' : 'connecting');
    };
  }, [id, close, scheduleFlush]);

  React.useEffect(() => {
    connect();
    return () => close();
  }, [connect, close]);

  React.useEffect(() => {
    document.title = `${t('matchDetail.logStreamViewerTitle', 'Live logs')} · #${id}${title ? ` · ${title}` : ''}`;
  }, [id, title, t]);

  const needle = filter.trim().toLowerCase();
  const shown = React.useMemo(
    () => (needle ? lines.filter(r => r.line.toLowerCase().includes(needle)) : lines),
    [lines, needle]
  );

  // Virtual window: track scroll position + viewport height of the log box
  const [scrollTop, setScrollTop] = React.useState(0);
  const [viewH, setViewH] = React.useState(800);
  const stickRef = React.useRef(true); // follow the tail while the user is at the bottom
  React.useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setViewH(el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const onScroll = (e) => {
    const el = e.currentTarget;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < ROW_H * 2;
    setScrollTop(el.scrollTop);
  };

  // Auto-scroll to the newest line unless paused or the user scrolled up
  React.useLayoutEffect(() => {
    const el = boxRef.current;
    if (!paused && stickRef.current && el) {
      el.scrollTop = el.scrollHeight;
      setScrollTop(el.scrollTop);
    }
  }, [shown, paused]);

  const first = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const last = Math.min(shown.length, Math.ceil((scrollTop + viewH) / ROW_H) + OVERSCAN);
  const visible = shown.slice(first, last);

  const togglePause = () => {
    const next = !paused;
    pausedRef.current = next;
    setPaused(next);
    if (!next) setLines(bufRef.current.slice());
  };

  const clearLines = () => { bufRef.current = []; setLines([]); };

  const download = () => {
    const text = shown.map(r => `${r.ts}\tM${(r.map_index || 0) + 1}\t${r.line}`).join('\n') + '\n';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    a.download = `match-${id}-logs.log`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const accent = '#2dd4bf';
  const statusMeta = {
    connecting: { color: '#fbbf24', label: t('matchDetail.logStreamStatusConnecting', 'Connecting…') },
    live: { color: '#22c55e', label: t('matchDetail.logStreamStatusLive', 'Live') },
    error: { color: '#ef4444', label: t('matchDetail.logStreamStatusError', 'Disconnected') },
    revoked: { color: '#ef4444', label: t('matchDetail.logStreamStatusRevoked', 'Token revoked') },
    denied: { color: '#ef4444', label: t('matchDetail.logStreamLoadError', 'Could not load the stream token.') },
  }[status];
  // Connected to a match that isn't live: show the match's real status instead of "Live"
  const MATCH_STATUS_COLORS = { pending: '#fbbf24', finished: '#8b8b9e', cancelled: '#ef4444' };
  const badge = status === 'live' && matchStatus && matchStatus !== 'live'
    ? { color: MATCH_STATUS_COLORS[matchStatus] || '#8b8b9e', label: t(`matchStatus.${matchStatus}`, matchStatus), glow: false }
    : { ...statusMeta, glow: status === 'live' };
  const btn = (bg, border, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
    background: bg, border: `1px solid ${border}`, borderRadius: '6px', color,
    padding: '0.35rem 0.7rem', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer',
    letterSpacing: '0.04em', whiteSpace: 'nowrap', fontFamily: 'var(--font-sans)',
  });
  const neutral = btn('rgba(255,255,255,0.04)', 'rgba(255,255,255,0.1)', 'var(--text-secondary, #8b8b9e)');

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-void, #0a0a0f)', color: 'var(--text-primary, #e8e8ed)' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.02)', flexWrap: 'wrap' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 11a9 9 0 0 1 9 9" />
          <path d="M4 4a16 16 0 0 1 16 16" />
          <circle cx="5" cy="19" r="1" />
        </svg>
        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: accent, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {t('matchDetail.logStreamViewerTitle', 'Live logs')}
        </span>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary, #8b8b9e)' }}>
          #{id}{title ? ` · ${title}` : ''}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.72rem', fontWeight: 700 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: badge.color, boxShadow: badge.glow ? '0 0 6px #22c55e' : 'none' }} />
          {badge.label}
        </span>
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted, #4a4a5a)' }}>
          {needle
            ? t('matchDetail.logStreamShowing', '{{shown}} of {{count}} lines', { shown: shown.length, count: lines.length })
            : t('matchDetail.logStreamLineCount', '{{count}} lines', { count: lines.length })}
        </span>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder={t('matchDetail.logStreamFilterPlaceholder', 'Filter lines…')}
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'var(--text-primary, #e8e8ed)', padding: '0.35rem 0.6rem', fontSize: '0.75rem', fontFamily: 'var(--font-mono)', width: 220 }}
          />
          {(status === 'error' || status === 'revoked') && (
            <button onClick={connect} style={btn('rgba(45,212,191,0.1)', 'rgba(45,212,191,0.25)', accent)}>
              {t('matchDetail.logStreamReconnect', 'Reconnect')}
            </button>
          )}
          <button onClick={togglePause} style={neutral}>
            {paused ? t('matchDetail.logStreamResume', 'Resume') : t('matchDetail.logStreamPause', 'Pause')}
          </button>
          <button onClick={clearLines} style={neutral}>{t('matchDetail.logStreamClear', 'Clear')}</button>
          <button onClick={download} disabled={shown.length === 0} style={neutral}>{t('matchDetail.logStreamDownload', 'Download')}</button>
          <Link to={`/admin/matches/${id}`} style={{ ...neutral, textDecoration: 'none' }}>
            {t('matchDetail.logStreamBackToMatch', 'Match page')}
          </Link>
        </div>
      </div>

      {/* Log lines */}
      <div ref={boxRef} onScroll={onScroll} style={{ flex: 1, overflow: 'auto', padding: '0.6rem 1rem', background: 'rgba(0,0,0,0.25)', fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>
        {shown.length === 0 ? (
          <div style={{ color: 'var(--text-muted, #4a4a5a)', fontStyle: 'italic' }}>
            {needle && lines.length > 0
              ? t('matchDetail.logStreamNoMatches', 'No lines match the filter.')
              : status === 'live' ? t('matchDetail.logStreamWaiting', 'Waiting for log lines…') : statusMeta.label}
          </div>
        ) : (
          <div style={{ position: 'relative', height: shown.length * ROW_H, minWidth: '100%', width: 'max-content' }}>
            {visible.map((row, i) => <LogRow key={row.id} row={row} top={(first + i) * ROW_H} />)}
          </div>
        )}
      </div>
    </div>
  );
}
